// One loading state for every async button (Confirm, Confirm all, Settle up, Dispute, Accept,
// Decline, Save, Send, the add-a-card steps): on tap the button is disabled and keeps its width; a
// small spinner shows only after SPINNER_DELAY_MS, so quick saves never flash. With reduced motion
// there is no spinner, the label reads "Saving..." instead. A second tap while busy does nothing.
// On failure the button comes back and the error shows under it (never a toast); on success the
// caller fades out what it saved (fadeOut()). DOM access goes through BusyDeps, so the rules are
// tested without a browser (test/busy.test.ts).

export const SPINNER_DELAY_MS = 150;
export const FADE_MS = 200;
export const SAVING_TEXT = 'Saving...';

/** The parts of a button the helper touches (an HTMLButtonElement in the app, a fake in tests). */
export interface BusyButton {
  disabled: boolean;
  dataset: Record<string, string | undefined>;
  textContent: string | null;
  classList: { add(c: string): void; remove(c: string): void };
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
  style: { width: string };
  readonly offsetWidth: number;
}

export interface BusyDeps {
  schedule(fn: () => void, ms: number): unknown;
  cancel(handle: unknown): void;
  reducedMotion(): boolean;
  /** Show (or clear, with null) the inline error under the button. */
  showError(button: BusyButton, message: string | null): void;
}

export type BusyResult<T> = { ok: true; value: T } | { ok: false; skipped?: true; error?: unknown };

/** Is this button already saving? (A tap on it then does nothing.) */
export const isBusy = (b: Pick<BusyButton, 'dataset'>) => b.dataset.busy === '1';

/**
 * Run `work` behind `button`. Returns ok with its value, or ok: false (skipped for a second tap
 * while busy, else with the error, which is already shown under the button).
 */
export async function runBusy<T>(button: BusyButton, work: () => Promise<T>, deps: BusyDeps, errorText: (e: unknown) => string): Promise<BusyResult<T>> {
  if (isBusy(button)) return { ok: false, skipped: true };
  const label = button.textContent;
  const width = button.style.width;
  button.dataset.busy = '1';
  // Fixed width first, so neither the spinner nor "Saving..." moves anything around it.
  button.style.width = `${button.offsetWidth}px`;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  deps.showError(button, null);
  const reduced = deps.reducedMotion();
  const timer = deps.schedule(() => {
    if (!isBusy(button)) return;
    if (reduced) button.textContent = SAVING_TEXT;
    else button.classList.add('is-busy');
  }, SPINNER_DELAY_MS);
  const restore = () => {
    deps.cancel(timer);
    delete button.dataset.busy;
    button.classList.remove('is-busy');
    button.removeAttribute('aria-busy');
    if (reduced) button.textContent = label;
    button.style.width = width;
  };
  try {
    const value = await work();
    restore();
    // Still disabled: what it saved is about to fade out or re-render.
    return { ok: true, value };
  } catch (error) {
    restore();
    button.disabled = false;
    deps.showError(button, errorText(error));
    return { ok: false, error };
  }
}

/** How long a saved row takes to fade out (none with reduced motion). */
export const fadeMs = (reducedMotion: boolean) => (reducedMotion ? 0 : FADE_MS);
